<template>
  <div class="sp">
    <div class="sp-chart">
      <apexchart :key="chartKey" v-if="slices.length" type="donut" height="100%" :options="options" :series="slices.map((s) => s.qty)"/>
      <div v-else class="w-empty"><i class="fas fa-leaf"></i>{{ stats.byTreeType ? 'No species yet' : 'Loading…' }}</div>
    </div>
    <div class="sp-list">
      <div v-for="(s, i) in slices" :key="s.name" class="sp-row w-row-in" :style="{ animationDelay: (i * 0.06) + 's' }">
        <span class="sp-dot" :style="{ background: s.color }"></span>
        <span class="sp-name" :title="s.name">{{ s.name }}</span>
        <span class="sp-pct">{{ fmt(s.pct, 1) }}%</span>
        <span class="sp-co2">{{ compact(s.co2) }} t</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { apexBase, compact, fmt, palette, seriesColors, themeState } from '../theme';

const TOP = 7;

export default {
  name: 'Species',
  components: { apexchart: VueApexCharts },
  props: { stats: { type: Object, required: true } },
  computed: {
    // a theme or data change re-creates the chart: ApexCharts does not always redraw mixed charts on update
    chartKey() { return `${themeState.tick}:${JSON.stringify(this.slices.map((s) => s.qty))}`; },
    slices() {
      const rows = (this.stats.byTreeType || []).map((r) => ({ name: r.treeType, qty: Number(r.qty) || 0, co2: (Number(r.totalCo2Kg) || 0) / 1000, color: r.color }));
      const total = rows.reduce((a, r) => a + r.qty, 0) || 1;
      const colors = seriesColors();
      const top = rows.slice(0, TOP).map((r, i) => ({ ...r, color: /^#[0-9a-f]{3,8}$/i.test(r.color || '') ? r.color : colors[i % colors.length] }));
      const rest = rows.slice(TOP);
      if (rest.length) top.push({ name: `${rest.length} other species`, qty: rest.reduce((a, r) => a + r.qty, 0), co2: rest.reduce((a, r) => a + r.co2, 0), color: palette().muted });
      return top.map((r) => ({ ...r, pct: (r.qty / total) * 100 }));
    },
    options() {
      void themeState.tick;
      const p = palette();
      const total = this.slices.reduce((a, s) => a + s.qty, 0);
      return apexBase({
        labels: this.slices.map((s) => s.name),
        colors: this.slices.map((s) => s.color),
        legend: { show: false },
        stroke: { width: 3, colors: [p.card] },
        plotOptions: {
          pie: {
            expandOnClick: true,
            donut: {
              size: '72%',
              labels: {
                show: true,
                name: { fontSize: '12px', color: p.muted, offsetY: 18 },
                value: { fontSize: '22px', fontWeight: 800, color: p.heading, offsetY: -12, formatter: (v) => compact(v) },
                total: { show: true, label: 'trees', color: p.muted, fontSize: '12px', formatter: () => compact(total) },
              },
            },
          },
        },
        tooltip: { y: { formatter: (v) => `${fmt(v)} trees` } },
      });
    }
  },
  methods: { fmt, compact }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';

.sp {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 14px 12px;
}

.sp-chart {
  position: relative;
  flex: 1 1 55%;
  min-height: 140px;
}

.sp-list {
  flex: 0 1 auto;
  overflow: auto;
  max-height: 45%;
  padding-top: 6px;
  scrollbar-width: thin;
}

.sp-row {
  display: grid;
  grid-template-columns: 12px 1fr auto auto;
  align-items: center;
  gap: 8px;
  padding: 4px 4px;
  font-size: 0.78rem;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover { background: var(--bg-secondary); }
}

.sp-dot { width: 10px; height: 10px; border-radius: 50%; }
.sp-name { color: var(--text-main); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sp-pct { color: var(--text-heading); font-weight: 700; font-variant-numeric: tabular-nums; }
.sp-co2 { color: var(--text-muted); font-variant-numeric: tabular-nums; min-width: 52px; text-align: right; }
</style>
